<template>
  <div class="wfp-form-page">

    <!-- ── Page header ── -->
    <div class="form-page-header">
      <div>
        <h5 class="form-page-title">Recruitment</h5>
        <p class="form-page-breadcrumb">
          <span class="breadcrumb-link" @click="goBack">Workforce Plan</span>
          <span class="breadcrumb-sep"> › </span>
          <span>{{ isEdit ? 'Edit Plan' : 'New Plan' }}</span>
        </p>
      </div>
      <div class="form-action-group">
        <button class="btn-outline" @click="goBack" :disabled="saving">Cancel</button>
        <button class="btn-gray" :disabled="saving" @click="handleSubmit('draft')">
          {{ saving ? 'Saving…' : 'Save as Draft' }}
        </button>
        <button class="btn-primary" :disabled="saving" @click="handleSubmit('pending_approval')">
          {{ saving ? 'Submitting…' : 'Submit for Approval' }}
          <font-awesome-icon icon="arrow-right" />
        </button>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="page-loading">
      <span class="spinner"></span>
      <p>{{ isEdit ? 'Loading plan…' : 'Loading form…' }}</p>
    </div>

    <div v-else>
      <!-- ── Section 1: Basic Information ── -->
      <div class="form-section">
        <h2 class="section-num-title">1. Basic Information</h2>
        <div class="form-grid-2">

          <div class="form-field">
            <label class="form-label">Workforce Plan Name <span class="req">*</span></label>
            <input type="text" class="form-input" :class="{ 'form-input--err': errors.planName }"
              v-model="form.planName" placeholder="e.g. Engineering Q3 Expansion" />
            <span v-if="errors.planName" class="err-msg">{{ errors.planName }}</span>
          </div>

          <div class="form-field">
            <label class="form-label">Created By</label>
            <input type="text" class="form-input form-input--readonly" :value="createdByName" readonly />
          </div>

          <div class="form-field">
            <label class="form-label">Department <span class="req">*</span></label>
            <select class="form-input" :class="{ 'form-input--err': errors.department }" v-model="form.department">
              <option value="">Select a department</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
            <span v-if="errors.department" class="err-msg">{{ errors.department }}</span>
          </div>

          <div class="form-field">
            <label class="form-label">Location <span class="req">*</span></label>
            <select class="form-input" :class="{ 'form-input--err': errors.location }" v-model="form.location">
              <option value="">Select a location</option>
              <option v-for="l in locations" :key="l.id" :value="l.id">{{ l.name }}</option>
            </select>
            <span v-if="errors.location" class="err-msg">{{ errors.location }}</span>
          </div>

          <div class="form-field">
            <label class="form-label">Start Date <span class="req">*</span></label>
            <input type="date" class="form-input" :class="{ 'form-input--err': errors.startDate }"
              v-model="form.startDate" />
            <span v-if="errors.startDate" class="err-msg">{{ errors.startDate }}</span>
          </div>

          <div class="form-field">
            <label class="form-label">End Date <span class="req">*</span></label>
            <input type="date" class="form-input" :class="{ 'form-input--err': errors.endDate }"
              v-model="form.endDate" />
            <span v-if="errors.endDate" class="err-msg">{{ errors.endDate }}</span>
          </div>

          <div class="form-field" :class="{ 'form-field--err': errors.planningPeriod }">
            <label class="form-label">Planning Period <span class="req">*</span></label>
            <select class="form-input" :class="{ 'form-input--err': errors.planningPeriod }"
              v-model="form.planningPeriod">
              <option value="" disabled>Select planning period</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
            <span v-if="errors.planningPeriod" class="err-msg">{{ errors.planningPeriod }}</span>
          </div>

        </div>
      </div>

      <!-- ── Section 2: Plan Summary ── -->
      <div class="form-section">
        <h2 class="section-num-title">2. Plan Summary</h2>
        <div class="form-grid-2">

          <div class="form-field">
            <label class="form-label">Total Headcount Required <span class="auto-tag">auto</span></label>
            <input type="text" class="form-input form-input--readonly" :value="totalHeadcount" readonly />
          </div>

          <div class="form-field">
            <label class="form-label">Estimated Budget <span class="auto-tag">auto</span></label>
            <input type="text" class="form-input form-input--readonly" :value="fmtCurrency(totalBudget)" readonly />
          </div>

          <div class="form-field" :class="{ 'form-field--err': errors.priorityLevel }">
            <label class="form-label">Priority Level <span class="req">*</span></label>
            <select class="form-input" :class="{ 'form-input--err': errors.priorityLevel }"
              v-model="form.priorityLevel">
              <option value="" disabled>Select priority level</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <span v-if="errors.priorityLevel" class="err-msg">{{ errors.priorityLevel }}</span>
          </div>

          <div class="form-field" :class="{ 'form-field--err': errors.planType }">
            <label class="form-label">Plan Type <span class="req">*</span></label>
            <select class="form-input" :class="{ 'form-input--err': errors.planType }" v-model="form.planType">
              <option value="" disabled>Select plan type</option>
              <option value="expansion">New Expansion</option>
              <option value="replacement">Replacement</option>
              <option value="restructuring">Restructuring</option>
            </select>
            <span v-if="errors.planType" class="err-msg">{{ errors.planType }}</span>
          </div>

        </div>
      </div>

      <!-- ── Section 3: Role Details ── -->
      <div class="form-section">
        <div class="section-head-row">
          <h2 class="section-num-title" style="margin:0">3. Role Details</h2>
          <button type="button" class="btn-add-role" @click="addRole">+ Add Role</button>
        </div>

        <div v-for="(role, index) in roles" :key="role._id" class="role-card">
          <div class="role-card-header">
            <span class="role-card-label">Role {{ index + 1 }}</span>
            <button type="button" class="role-remove-btn" :disabled="roles.length === 1" @click="removeRole(index)">
              <font-awesome-icon icon="trash" />
            </button>
          </div>

          <div class="form-grid-2">
            <!-- Job Title (position) -->
            <div class="form-field">
              <label class="form-label">Job Title <span class="req">*</span></label>
              <select class="form-input" :class="{ 'form-input--err': errors[`role_${index}_jobTitle`] }"
                v-model="role.jobTitle">
                <option value="">Select position</option>
                <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
              <span v-if="errors[`role_${index}_jobTitle`]" class="err-msg">{{ errors[`role_${index}_jobTitle`] }}</span>
            </div>

            <!-- Number of Positions -->
            <div class="form-field">
              <label class="form-label">Number of Positions <span class="req">*</span></label>
              <input type="number" class="form-input" :class="{ 'form-input--err': errors[`role_${index}_numberOfPositions`] }"
                v-model="role.numberOfPositions" min="1" placeholder="e.g. 5" />
              <span v-if="errors[`role_${index}_numberOfPositions`]" class="err-msg">{{ errors[`role_${index}_numberOfPositions`] }}</span>
            </div>

            <!-- Employment Type -->
            <div class="form-field">
              <label class="form-label">Employment Type <span class="req">*</span></label>
              <select class="form-input" :class="{ 'form-input--err': errors[`role_${index}_employmentType`] }"
                v-model="role.employmentType">
                <option value="">Select type</option>
                <option value="Permanent">Permanent</option>
                <option value="Contract">Contract</option>
                <option value="Interns">Interns</option>
                <option value="Adhoc">Adhoc</option>
              </select>
              <span v-if="errors[`role_${index}_employmentType`]" class="err-msg">{{ errors[`role_${index}_employmentType`] }}</span>
            </div>

            <!-- Job Grade -->
            <div class="form-field">
              <label class="form-label">Job Grade / Level <span class="req">*</span></label>
              <select class="form-input" :class="{ 'form-input--err': errors[`role_${index}_jobGrade`] }"
                v-model="role.jobGrade">
                <option value="">Select grade</option>
                <option v-for="g in payGrades" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
              <span v-if="errors[`role_${index}_jobGrade`]" class="err-msg">{{ errors[`role_${index}_jobGrade`] }}</span>
            </div>

            <!-- Reporting Line -->
            <div class="form-field">
              <label class="form-label">Reporting Line <span class="req">*</span></label>
              <select class="form-input" :class="{ 'form-input--err': errors[`role_${index}_reportingLine`] }"
                v-model="role.reportingLine">
                <option value="">Select position</option>
                <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
              <span v-if="errors[`role_${index}_reportingLine`]" class="err-msg">{{ errors[`role_${index}_reportingLine`] }}</span>
            </div>

            <!-- Role Priority -->
            <div class="form-field">
              <label class="form-label">Role Priority <span class="req">*</span></label>
              <select class="form-input" :class="{ 'form-input--err': errors[`role_${index}_rolePriority`] }"
                v-model="role.rolePriority">
                <option value="">Select priority</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <span v-if="errors[`role_${index}_rolePriority`]" class="err-msg">{{ errors[`role_${index}_rolePriority`] }}</span>
            </div>

            <!-- Salary Min -->
            <div class="form-field">
              <label class="form-label">Salary Min <span class="req">*</span></label>
              <input type="number" class="form-input" :class="{ 'form-input--err': errors[`role_${index}_salaryMin`] }"
                v-model="role.salaryMin" min="0" placeholder="e.g. 50000" />
              <span v-if="errors[`role_${index}_salaryMin`]" class="err-msg">{{ errors[`role_${index}_salaryMin`] }}</span>
            </div>

            <!-- Salary Max -->
            <div class="form-field">
              <label class="form-label">Salary Max <span class="req">*</span></label>
              <input type="number" class="form-input" :class="{ 'form-input--err': errors[`role_${index}_salaryMax`] }"
                v-model="role.salaryMax" min="0" placeholder="e.g. 100000" />
              <span v-if="errors[`role_${index}_salaryMax`]" class="err-msg">{{ errors[`role_${index}_salaryMax`] }}</span>
            </div>

            <!-- Required Skills (full width) -->
            <div class="form-field form-field--full">
              <label class="form-label">Required Skills <span class="req">*</span> <span class="hint">(comma separated)</span></label>
              <input type="text" class="form-input" :class="{ 'form-input--err': errors[`role_${index}_requiredSkills`] }"
                v-model="role.requiredSkills" placeholder="e.g. JavaScript, React, Node.js" />
              <span v-if="errors[`role_${index}_requiredSkills`]" class="err-msg">{{ errors[`role_${index}_requiredSkills`] }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section 4: Budget Summary ── -->
      <div class="form-section">
        <h2 class="section-num-title">4. Budget Summary</h2>
        <div class="form-grid-2">

          <div class="form-field">
            <label class="form-label">Total Estimated Cost <span class="auto-tag">auto</span></label>
            <input type="text" class="form-input form-input--readonly" :value="fmtCurrency(totalBudget)" readonly />
          </div>

          <div class="form-field">
            <label class="form-label">Monthly Budget Allocation <span class="auto-tag">auto</span></label>
            <input type="text" class="form-input form-input--readonly" :value="fmtCurrency(monthlyAllocation)" readonly placeholder="Auto calculated from planning period" />
          </div>

          <div class="form-field">
            <label class="form-label">Budget Owner <span class="req">*</span></label>
            <input type="text" class="form-input" :class="{ 'form-input--err': errors.budgetOwner }"
              v-model="form.budgetOwner" placeholder="e.g. John Doe" />
            <span v-if="errors.budgetOwner" class="err-msg">{{ errors.budgetOwner }}</span>
          </div>

          <div class="form-field form-field--center">
            <div :class="{ 'checkbox-err-wrap': errors.budgetApproved }">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.budgetApproved" />
                <span>Budget Approved</span>
              </label>
              <span v-if="errors.budgetApproved" class="err-msg">{{ errors.budgetApproved }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Section 5: Justification ── -->
      <div class="form-section">
        <h2 class="section-num-title">5. Justification</h2>
        <div class="form-grid-1">

          <div class="form-field">
            <label class="form-label">Reason for Workforce Plan <span class="req">*</span></label>
            <textarea class="form-input" :class="{ 'form-input--err': errors.reasonForPlan }"
              v-model="form.reasonForPlan" rows="4" placeholder="Describe the rationale"></textarea>
            <span v-if="errors.reasonForPlan" class="err-msg">{{ errors.reasonForPlan }}</span>
          </div>

          <div class="form-field">
            <label class="form-label">Business Objective Alignment <span class="req">*</span></label>
            <textarea class="form-input" :class="{ 'form-input--err': errors.businessObjectiveAlignment }"
              v-model="form.businessObjectiveAlignment" rows="4"
              placeholder="Describe the alignment with business objectives"></textarea>
            <span v-if="errors.businessObjectiveAlignment" class="err-msg">{{ errors.businessObjectiveAlignment }}</span>
          </div>

        </div>
      </div>

      <!-- ── Bottom actions ── -->
      <div class="bottom-actions">
        <button class="btn-outline" @click="goBack" :disabled="saving">Cancel</button>
        <button v-if="!isEdit" class="btn-gray" :disabled="saving" @click="handleSubmit('draft')">
          {{ saving ? 'Saving…' : 'Save as Draft' }}
        </button>
        <button class="btn-primary" :disabled="saving" @click="handleSubmit('pending_approval')">
          {{ saving ? 'Submitting…' : (isEdit ? 'Save & Submit' : 'Submit for Approval') }}
          <font-awesome-icon icon="arrow-right" />
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

function blankRole() {
  return {
    _id: Date.now() + Math.random(),
    jobTitle: '',
    numberOfPositions: '',
    employmentType: '',
    jobGrade: '',
    reportingLine: '',
    rolePriority: '',
    salaryMin: '',
    salaryMax: '',
    requiredSkills: '',
  };
}

export default {
  name: 'WorkforcePlanForm',
  data() {
    return {
      loading: false,
      saving: false,
      errors: {},

      departments: [],
      locations: [],
      payGrades: [],
      positions: [],

      form: {
        planName: '',
        department: '',
        location: '',
        startDate: '',
        endDate: '',
        planningPeriod: '',
        priorityLevel: '',
        planType: '',
        budgetOwner: '',
        budgetApproved: false,
        reasonForPlan: '',
        businessObjectiveAlignment: '',
      },

      roles: [blankRole()],
    };
  },

  computed: {
    isEdit() {
      return Boolean(this.$route?.params?.planId);
    },
    planId() {
      return this.$route?.params?.planId || null;
    },
    loggedUser() {
      return this.$store?.state?.getLoggedInUser || {};
    },
    createdByName() {
      const u = this.loggedUser;
      return [u.last_name, u.first_name].filter(Boolean).join(' ') || u.name || '';
    },
    totalHeadcount() {
      return this.roles.reduce((sum, r) => sum + (parseInt(r.numberOfPositions) || 0), 0);
    },
    totalBudget() {
      return this.roles.reduce((sum, r) => {
        const min = parseFloat(r.salaryMin) || 0;
        const max = parseFloat(r.salaryMax) || 0;
        const n   = parseInt(r.numberOfPositions) || 0;
        return sum + ((min + max) / 2) * n;
      }, 0);
    },
    monthlyAllocation() {
      const months = { monthly: 1, quarterly: 3, yearly: 12 }[this.form.planningPeriod] || 1;
      return this.totalBudget / months;
    },
  },

  async mounted() {
    this.loading = true;
    await this.fetchLookups();
    if (this.isEdit) await this.fetchPlan();
    this.loading = false;
  },

  methods: {
    async fetchLookups() {
      try {
        const [deptRes, locRes, gradeRes, posRes] = await Promise.all([
          axios.get('/payroll/department/active'),
          axios.get('/admin/location/active'),
          axios.get('/payroll/paygrade/active'),
          axios.get('/payroll/position/active'),
        ]);
        this.departments = deptRes.data?.data || [];
        this.locations   = locRes.data?.data  || [];
        this.payGrades   = gradeRes.data?.data || [];
        this.positions   = posRes.data?.data   || [];
      } catch (e) {
        this.$toast?.error('Failed to load form data');
      }
    },

    async fetchPlan() {
      try {
        const { data } = await axios.get(`/hr/recruitment/workforce-plan/${this.planId}`);
        const plan = data.data;
        if (!plan) return;

        this.form = {
          planName: plan.name || '',
          department: plan.department_id || '',
          location: plan.company_location_id || '',
          startDate: plan.start_date ? String(plan.start_date).split('T')[0] : '',
          endDate: plan.end_date ? String(plan.end_date).split('T')[0] : '',
          planningPeriod: plan.planing_period || '',
          priorityLevel: String(plan.priority || '').toLowerCase(),
          planType: plan.plan_type || '',
          budgetOwner: plan.budget_owner || '',
          budgetApproved: Boolean(plan.budget_approved),
          reasonForPlan: plan.reason || '',
          businessObjectiveAlignment: plan.objectives || '',
        };

        if (plan.roles?.length) {
          this.roles = plan.roles.map(r => ({
            _id: r.id,
            jobTitle: r.position_id || '',
            numberOfPositions: String(r.position_count || ''),
            employmentType: r.employment_type || '',
            jobGrade: r.pay_grade_id || '',
            reportingLine: r.reporting_position_id || '',
            rolePriority: this.toPriorityOption(r.priority),
            salaryMin: String(r.min_salary || ''),
            salaryMax: String(r.max_salary || ''),
            requiredSkills: r.objectives || '',
          }));
        }
      } catch (e) {
        this.$toast?.error('Failed to load plan for editing');
      }
    },

    toPriorityOption(v) {
      const s = String(v || '').toLowerCase();
      if (!s) return '';
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    addRole() {
      this.roles.push(blankRole());
    },

    removeRole(index) {
      if (this.roles.length === 1) return;
      this.roles.splice(index, 1);
    },

    fmtCurrency(v) {
      if (!v && v !== 0) return '';
      return '₦' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    validate() {
      const errs = {};

      if (!this.form.planName?.trim())                       errs.planName = 'Workforce Plan Name is required';
      if (!this.form.department)                             errs.department = 'Department is required';
      if (!this.form.location)                               errs.location = 'Location is required';
      if (!this.form.startDate)                              errs.startDate = 'Start Date is required';
      if (!this.form.endDate)                                errs.endDate = 'End Date is required';
      if (this.form.startDate && this.form.endDate && this.form.endDate < this.form.startDate)
                                                             errs.endDate = 'End Date must be after Start Date';
      if (!this.form.planningPeriod)                         errs.planningPeriod = 'Planning Period is required';
      if (!this.form.priorityLevel)                          errs.priorityLevel = 'Priority Level is required';
      if (!this.form.planType)                               errs.planType = 'Plan Type is required';
      if (!this.form.budgetOwner?.trim())                    errs.budgetOwner = 'Budget Owner is required';
      if (!this.form.budgetApproved)                         errs.budgetApproved = 'Please confirm Budget Approved before submitting';
      if (!this.form.reasonForPlan?.trim())                  errs.reasonForPlan = 'Reason for Workforce Plan is required';
      if (!this.form.businessObjectiveAlignment?.trim())     errs.businessObjectiveAlignment = 'Business Objective Alignment is required';

      this.roles.forEach((role, i) => {
        if (!role.jobTitle)                                  errs[`role_${i}_jobTitle`] = `Role ${i+1}: Job Title is required`;
        if (!role.numberOfPositions || parseInt(role.numberOfPositions) < 1)
                                                             errs[`role_${i}_numberOfPositions`] = `Role ${i+1}: Number of Positions must be ≥ 1`;
        if (!role.employmentType)                            errs[`role_${i}_employmentType`] = `Role ${i+1}: Employment Type is required`;
        if (!role.jobGrade)                                  errs[`role_${i}_jobGrade`] = `Role ${i+1}: Job Grade is required`;
        if (!role.reportingLine)                             errs[`role_${i}_reportingLine`] = `Role ${i+1}: Reporting Line is required`;
        if (!role.rolePriority)                              errs[`role_${i}_rolePriority`] = `Role ${i+1}: Role Priority is required`;
        if (!role.salaryMin && role.salaryMin !== 0)         errs[`role_${i}_salaryMin`] = `Role ${i+1}: Salary Min is required`;
        if (!role.salaryMax && role.salaryMax !== 0)         errs[`role_${i}_salaryMax`] = `Role ${i+1}: Salary Max is required`;
        if (role.salaryMin && role.salaryMax && parseFloat(role.salaryMax) < parseFloat(role.salaryMin))
                                                             errs[`role_${i}_salaryMax`] = `Role ${i+1}: Salary Max must be ≥ Salary Min`;
        if (!role.requiredSkills?.trim())                    errs[`role_${i}_requiredSkills`] = `Role ${i+1}: Required Skills is required`;
      });

      this.errors = errs;
      return errs;
    },

    async handleSubmit(status) {
      const errs = this.validate();
      if (Object.keys(errs).length) {
        this.$toast?.error('Please fix the validation errors before submitting');
        // Scroll to first error
        this.$nextTick(() => {
          const el = document.querySelector('.form-input--err');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      const months = { monthly: 1, quarterly: 3, yearly: 12 }[this.form.planningPeriod] || 1;

      const payload = {
        name:                      this.form.planName,
        department_id:             this.form.department,
        company_location_id:       this.form.location,
        start_date:                this.form.startDate,
        end_date:                  this.form.endDate,
        planing_period:            this.form.planningPeriod,
        priority:                  this.form.priorityLevel,
        plan_type:                 this.form.planType,
        total_headcount:           this.totalHeadcount,
        estimated_budget:          this.totalBudget,
        monthly_budget_allocation: this.totalBudget / months,
        budget_owner:              this.form.budgetOwner,
        budget_approved:           this.form.budgetApproved,
        reason:                    this.form.reasonForPlan,
        objectives:                this.form.businessObjectiveAlignment,
        status:                    status,
        roles: this.roles.map(r => ({
          position_id:             r.jobTitle,
          position_count:          parseInt(r.numberOfPositions) || 1,
          employment_type:         r.employmentType,
          pay_grade_id:            r.jobGrade,
          reporting_position_id:   r.reportingLine,
          priority:                r.rolePriority,
          min_salary:              parseFloat(r.salaryMin) || 0,
          max_salary:              parseFloat(r.salaryMax) || 0,
          objectives:              r.requiredSkills,
        })),
      };

      this.saving = true;
      try {
        if (this.isEdit) {
          await axios.put(`/hr/recruitment/workforce-plan/${this.planId}`, payload);
        } else {
          await axios.post('/hr/recruitment/workforce-plan', payload);
        }
        const action = this.isEdit ? 'updated' : (status === 'draft' ? 'saved as draft' : 'submitted for approval');
        this.$toast?.success(`Plan ${action} successfully!`);
        this.$router.push(this.$url('/hr/recruitment'));
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to save plan');
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push(this.$url('/hr/recruitment'));
    },
  },
};
</script>

<style scoped>
.wfp-form-page { max-width: 960px; margin: 0 auto; padding-bottom: 60px; }

/* Header */
.form-page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 24px; }
.form-page-title  { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.form-page-breadcrumb { font-size: 13px; color: #7a90b0; margin: 0; }
.breadcrumb-link  { cursor: pointer; color: #7a90b0; text-decoration: underline; }
.breadcrumb-link:hover { color: #004AAD; }
.breadcrumb-sep   { margin: 0 4px; }
.form-action-group { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

/* Loading */
.page-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 12px; }
.spinner { width: 32px; height: 32px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Sections */
.form-section { background: #fff; border-radius: 12px; border: 1px solid #e4eaf2; padding: 24px; margin-bottom: 16px; }
.section-num-title { font-size: 15px; font-weight: 700; color: #1a2b45; margin: 0 0 20px; }
.section-head-row  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }

/* Grid */
.form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-grid-1 { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (max-width: 640px) { .form-grid-2 { grid-template-columns: 1fr; } }

/* Fields */
.form-field { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: 1 / -1; }
.form-field--center { justify-content: flex-end; }
.form-field--err { }
.form-label { font-size: 12px; font-weight: 600; color: #4a5e78; }
.form-input  { width: 100%; padding: 9px 12px; border: 1px solid #dde5f0; border-radius: 8px; font-size: 13px; color: #1a2b45; outline: none; box-sizing: border-box; background: #fff; }
.form-input:focus { border-color: #004AAD; }
.form-input--readonly { background: #f8fafc; color: #7a90b0; cursor: default; }
.form-input--err { border-color: #dc2626 !important; background: #fff5f5; }
textarea.form-input { resize: vertical; }
.err-msg { font-size: 11px; font-weight: 600; color: #dc2626; }
.req  { color: #dc2626; }
.hint { color: #aab8cc; font-weight: 400; }
.auto-tag { display: inline-block; background: #f0f4f8; color: #7a90b0; font-size: 10px; font-weight: 700; border-radius: 4px; padding: 1px 5px; margin-left: 4px; vertical-align: middle; }

/* Roles */
.btn-add-role { display: inline-flex; align-items: center; height: 36px; padding: 0 16px; border: 1px solid #16a34a; background: #fff; color: #16a34a; font-size: 13px; font-weight: 600; border-radius: 8px; cursor: pointer; }
.btn-add-role:hover { background: #f0fdf4; }
.role-card { border: 1px solid #dde5f0; border-radius: 10px; padding: 18px; margin-bottom: 14px; }
.role-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.role-card-label  { font-size: 13px; font-weight: 700; color: #1a2b45; }
.role-remove-btn  { background: none; border: none; cursor: pointer; color: #dc2626; font-size: 15px; padding: 4px 8px; border-radius: 6px; }
.role-remove-btn:disabled { opacity: .35; cursor: not-allowed; }
.role-remove-btn:not(:disabled):hover { background: #fee2e2; }

/* Checkbox */
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1a2b45; cursor: pointer; }
.checkbox-label input[type="checkbox"] { width: 18px; height: 18px; accent-color: #004AAD; cursor: pointer; }
.checkbox-err-wrap { display: flex; flex-direction: column; gap: 4px; padding: 10px 12px; border: 1px solid #dc2626; border-radius: 8px; background: #fff5f5; }

/* Buttons */
.btn-primary { display: inline-flex; align-items: center; gap: 8px; background: #004AAD; color: #fff; font-size: 13px; font-weight: 600; padding: 0 18px; height: 38px; border-radius: 8px; border: none; cursor: pointer; }
.btn-primary:hover:not(:disabled) { background: #003b8e; }
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.btn-gray    { display: inline-flex; align-items: center; gap: 8px; background: #f1f5f9; color: #1a2b45; font-size: 13px; font-weight: 600; padding: 0 18px; height: 38px; border-radius: 8px; border: 1px solid #dde5f0; cursor: pointer; }
.btn-gray:hover:not(:disabled) { background: #e4eaf2; }
.btn-gray:disabled { opacity: .6; cursor: not-allowed; }
.btn-outline  { display: inline-flex; align-items: center; gap: 8px; background: #fff; color: #1a2b45; font-size: 13px; font-weight: 600; padding: 0 16px; height: 38px; border-radius: 8px; border: 1px solid #dde5f0; cursor: pointer; }
.btn-outline:hover:not(:disabled) { background: #f8fafc; }
.btn-outline:disabled { opacity: .6; cursor: not-allowed; }

/* Bottom actions */
.bottom-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
</style>
